/**
 * 2026.1 — RATES (FY2025-26 — 1 Jul 2025 → 30 Jun 2026).
 *
 * Every value below is sourced from the ATO's own pages / instruments
 * (linked in each `source`). Numbers are the values applying to the
 * FY25-26 income year for Australian tradies and small businesses.
 *
 * IMPORTANT: this file is FROZEN once merged with `status: "confirmed"`.
 * For a rate change (e.g. new WFH hourly rate mid-year), spin up a new
 * ruleset version (`2026.2/`) — do NOT edit this file.
 */

import type { AtoRates } from "../types";

export const rates: AtoRates = {
  status: "confirmed",

  /**
   * GST rate — unchanged since 1 Jul 2000. Applies to taxable sales +
   * creditable acquisitions in Australia. Expressed as a percentage.
   * A consumer doing 1/11-of-tax-inclusive math should convert:
   *   gst_amount = total_incl / (1 + rates.gst.amount / 100)   // ex-GST
   *   gst_amount = total_incl * rates.gst.amount / (100 + rates.gst.amount) // GST portion
   */
  gst: {
    amount: 10,
    unit: "percent",
    source:
      "ATO — GST (rate 10% since 1 Jul 2000): https://www.ato.gov.au/businesses-and-organisations/gst-excise-and-indirect-taxes/gst",
  },

  /**
   * Working-from-home FIXED-RATE method — dollars per hour worked from
   * home. Covers electricity + gas + internet + mobile/home phone +
   * stationery/computer consumables in one flat rate. If a tradie uses
   * this method they can NOT separately claim those same expenses.
   *
   * Rate is 70c/hour for FY24-25 AND FY25-26. Prior rate (FY22-23,
   * FY23-24) was 67c/hour — surface as historical only.
   */
  wfhFixedRate: {
    amount: 0.70,
    unit: "AUD/hour",
    source:
      "ATO Fixed Rate Method + PCG 2023/1: https://www.ato.gov.au/individuals-and-families/income-deductions-offsets-and-records/deductions-you-can-claim/work-related-deductions/working-from-home-expenses/fixed-rate-method",
  },

  /**
   * Cents-per-kilometre method for car expenses. Max 5,000 business km
   * per car per income year. Covers ALL car running costs — including
   * depreciation — so no separate depreciation claim under this method.
   *
   * Rate is 88c/km for FY24-25 AND FY25-26. Announced 91c/km from 1 Jul
   * 2026 (FY26-27) — that will be captured in a future ruleset.
   */
  centsPerKm: {
    amount: 0.88,
    unit: "AUD/km",
    source:
      "ATO — Cents-per-kilometre method: https://www.ato.gov.au/individuals-and-families/income-deductions-offsets-and-records/deductions-you-can-claim/work-related-deductions/cars-transport-and-travel/motor-vehicle-and-car-expenses/expenses-for-a-car-you-own-or-lease/cents-per-kilometre-method",
  },

  /**
   * Concessional superannuation contributions cap — combined limit for
   * employer SG + salary sacrifice + personal deductible contributions
   * per member per financial year. Rises to $32,500 from 1 Jul 2026.
   */
  superConcessionalCap: {
    amount: 30000,
    unit: "AUD/year",
    source:
      "ATO — Concessional contributions cap: https://www.ato.gov.au/individuals-and-families/super-for-individuals-and-families/super/growing-and-keeping-track-of-your-super/caps-limits-and-tax-on-super-contributions/concessional-contributions-cap",
  },
};
